import {
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    TextField,
    Typography,
    FormControlLabel,
    FormGroup,
    styled,
    Switch,
    SwitchProps, CardActions, Container, Chip, Tooltip } from "@mui/material";
import {ChevronRight} from "@mui/icons-material";

import googleLogo from "@assets/images/google_g_logo.svg";
import {useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {useSnackbar} from "notistack";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { format } from "date-fns";
import {Link, useNavigate} from "react-router-dom";
import {urls} from "@routes";
import {useDialogs} from "@toolpad/core";
import IconButton from "@mui/material/IconButton";
import {Delete, SyncProblem} from "@mui/icons-material";


interface IntegrationRow {
    id: number
    email: string
    is_active: boolean
    created_at: string
}

interface PageData {
    google_auth_active: boolean
    data: IntegrationRow[]
    total_count: number
    total_pages: number
    current_page: number
    page_size: number
}

export default function AdminPostmasterFbl() {
    const { enqueueSnackbar } = useSnackbar();    
    const navigate = useNavigate();
	const dialogs = useDialogs();
    
    const [pageData, setPageData] = useState<PageData>();
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(50)
	const [searchQuery, setSearchQuery] = useState("")


    const pageDataQuery = useQuery({
		queryKey: ["integrations", currentPage, pageSize, searchQuery],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/google-integrations/?page=${currentPage}&page_size=${pageSize}&search=${searchQuery}`
			),
		refetchOnWindowFocus: false,
		retry: retryFn,
	})

    useEffect(() => {
        if (pageDataQuery.data) {
            setPageData(pageDataQuery.data.data as PageData)
        }
    }, [pageDataQuery.data])
    
    const googleAuthMutation = useMutation({
		mutationKey: ["startGoogleAuthFlow"],
		mutationFn: (body: { integration_id?: number } = {}) => 
            authenticateAndPostData("/admin/google-auth/start/", body),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			console.log(response);
			let action = response.data["action"];
			switch (action) {
				case "redirect":
					// Send user to authenticate.
					let url = response.data["url"];
					window.location.href = url;
					break;

				default:
					// Not handling 'reauthenticated' action.
					console.error(`Unknown/unhandled action '${action}'`);
					break;
			}
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(
				error.data.message || "Something went wrong on our side. Please try again in some time.",
				{variant: "error"}
			);
		}
	});

	// Mutation to disconnect google services (postmaster) integration.
	const googleAuthRevoke = useMutation({
		mutationKey: ["googleAuthRevoke"],
		mutationFn: (integrationId: number) => authenticateAndPostData("/admin/google-auth/revoke/", {integration_id: integrationId}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			enqueueSnackbar("Google Postmaster integration has been removed successfully.", {
				variant: "success",
			})
			pageDataQuery.refetch()
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error"
			});
		}
	});

	const handlePageChange = (page: number) => {
		setCurrentPage(page)
	}

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize)
		setCurrentPage(1)
	}

	const handleSearchChange = (search: string) => {
		setSearchQuery(search)
		setCurrentPage(1)
	}


	function timestampFormatter(params: ValueFormatterParams) {
			if (params.value === null) {
				return "---";
			}
			return format(new Date(params.value), "do MMM y, h:mm a");
		}

	const columnDefs: ColDef[] = [
		{
			field: "email",
			headerName: "Google Account Email",
			flex: 2,
		},
		{
			field: "is_active",
			headerName: "Status",
			flex: 1,
			cellRenderer: ({ value }: any) => (
				<Chip
					label={value ? "Active" : "Inactive"}
					color={value ? "success" : "error"}
					size="small"
				/>
			),
		},
		{
			field: "created_at",
			headerName: "Connected On",
			flex: 1,
			valueFormatter: timestampFormatter,
		},
		{
            field: "actions",
            headerName: "Actions",
            minWidth: 220,
            flex: 1,
            cellStyle: { display: 'flex', alignItems: 'center' },
            cellRenderer: (params: any) => {
                const isActive = params.data.is_active;

                const onDisconnectClick = async () => {
                    const confirmed = await dialogs.confirm(
                        `Are you sure you want to disconnect the postmaster account "${params.data.email}"? This action can't be undone.`,
                        {
                            title: "Disconnect Google Postmaster Account",
                            cancelText: "Cancel",
                            okText: "Confirm",
                        }
                    );

                    if (confirmed) {
                        googleAuthRevoke.mutate(params.data.id);
                    }
                };

                return (
                    <Stack direction="row" spacing={1} alignItems="center">     
                        {isActive ? (
                            /* --- Render View Domains if token is completely healthy --- */
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                onClick={() =>
                                    navigate(
                                        `${urls["adminGooglePostmasterPage"]}?integration_id=${params.data.id}`
                                    )
                                }
                            >
                                View Domains
                            </Button>
                        ) : (
                            /* --- FIX: Render Reconnect button if account token is expired --- */
                            <Tooltip 
                                title="Authorization expired. Click to re-authenticate with Google without deleting existing data."
                                arrow
                                placement="top"
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        disableElevation
                                        startIcon={<SyncProblem fontSize="small" />}
                                        disabled={googleAuthMutation.isPending}
                                        onClick={() => googleAuthMutation.mutate({ integration_id: params.data.id })}
                                    >
                                        Reconnect
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        
                        {/* --- Disconnect Button --- */}
                        <Tooltip title="Delete & Disconnect Account completely">
                            <span>
                                <IconButton
                                    color="error"
                                    size="small"
                                    disabled={googleAuthRevoke.isPending}
                                    onClick={onDisconnectClick}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];


    return (
    <Stack spacing={4} sx={{ height: "100%", overflow: "hidden" }}>
        {/* Header */}
        <Container>
            <Typography variant="h3" align="center" color="primary">
                Integrations
            </Typography>
            <Typography variant="subtitle1" align="center">
                Connect and manage your Google Postmaster accounts
            </Typography>
        </Container>

        {/* Google Postmaster Card */}
        <Card sx={{maxWidth: 345, padding: "1em"}}>
            <CardContent>
                <Box display="flex" alignItems="center">
                    <img
                        src={googleLogo}
                        alt="Google Logo"
                        style={{ width: 32, height: 32 }}
                    />
                    <Typography variant="h5" component="div" sx={{ml: 2}}>
                        Google Postmaster
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{color: 'text.secondary', mt: 2}}>
                    Track domain reputation, spam rate, and delivery health
                    directly from Google Postmaster.
                </Typography>
            </CardContent>

            <CardActions>
                <Button
                    variant="contained"
                    color="success"
                    disabled={googleAuthMutation.isPending}
                    onClick={() => googleAuthMutation.mutate({})}
                >
                    Connect new account
                </Button>
            </CardActions>
        </Card>

        {/* Table Section */}
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <ServerSideDataTable  
                columns={columnDefs}
                rows={pageData?.data ?? []}
                noRowsText="No Integration Available"
                loading={pageDataQuery.isFetching || pageDataQuery.isLoading}
            />
        </Box>
    </Stack>
);
}